import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTutorial } from '../../contexts/TutorialContext';
import { useNotificationModal } from '../../contexts/NotificationModalContext';
import TutorialSpotlight from './TutorialSpotlight';
import './Tutorial.css';

// Premium comparison data for the premium step
const PREMIUM_COMPARISON = [
  { feature: 'Lists', free: 'Up to 10', premium: 'Unlimited' },
  { feature: 'Places per list', free: 'Up to 30', premium: 'Unlimited' },
  { feature: 'Map view', free: false, premium: true },
  { feature: 'Place photos', free: false, premium: true },
  { feature: 'Opening hours', free: false, premium: true },
  { feature: 'Trip Planner', free: false, premium: true },
  { feature: 'Offline access', free: false, premium: true },
];

function Tutorial() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    prevStep,
    endTutorial,
    notifyAction,
    skipNextNavCheck,
    setSkipNextNavCheck,
  } = useTutorial();
  const { showAlert } = useNotificationModal();

  const location = useLocation();
  const [targetRect, setTargetRect] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const tooltipRef = useRef(null);
  const observerRef = useRef(null);
  const prevStepIndexRef = useRef(currentStepIndex);

  // Find and track the target element
  const updateTargetRect = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(currentStep.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right,
      });
    } else if (currentStep.targetFallback) {
      const fallback = document.querySelector(currentStep.targetFallback);
      if (fallback) {
        const rect = fallback.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          bottom: rect.bottom,
          right: rect.right,
        });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  // Set up mutation observer to watch for DOM changes
  useEffect(() => {
    if (!isActive) {
      setIsReady(false);
      return;
    }

    // Reset ready state when step changes
    setIsReady(false);
    setTargetRect(null);

    // For hideTooltip steps, we can be ready immediately (floating pill doesn't need positioning)
    if (currentStep?.hideTooltip) {
      setIsReady(true);
      return;
    }

    // For center placement steps (no target), ready immediately
    if (!currentStep?.target && currentStep?.placement === 'center') {
      setIsReady(true);
      return;
    }

    // Wait for DOM to settle, then find element and mark ready
    const timer = setTimeout(() => {
      updateTargetRect();
      // Use double rAF to ensure layout is complete before showing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsReady(true);

          // Only start observing after we're ready
          observerRef.current = new MutationObserver(() => {
            updateTargetRect();
          });
          observerRef.current.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
          });
        });
      });
    }, 250);

    // Update on scroll and resize
    window.addEventListener('scroll', updateTargetRect, true);
    window.addEventListener('resize', updateTargetRect);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('scroll', updateTargetRect, true);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [isActive, currentStep, updateTargetRect]);

  // Listen for navigation changes to advance steps
  useEffect(() => {
    if (!isActive || !currentStep?.waitFor) {
      // Update ref for non-waitFor steps
      prevStepIndexRef.current = currentStepIndex;
      return;
    }

    if (currentStep.waitFor.startsWith('navigation:')) {
      // Skip if we just went back - prevents immediate re-advance
      // Check BEFORE updating the ref
      const wentBack = currentStepIndex < prevStepIndexRef.current;

      if (skipNextNavCheck || wentBack) {
        setSkipNextNavCheck(false);
        prevStepIndexRef.current = currentStepIndex;
        return;
      }

      prevStepIndexRef.current = currentStepIndex;

      const expectedPath = currentStep.waitFor.replace('navigation:', '');
      if (location.pathname.startsWith(expectedPath)) {
        // If step has a waitForSuffix, also check that the path ends with it
        if (currentStep.waitForSuffix) {
          if (location.pathname.endsWith(currentStep.waitForSuffix)) {
            notifyAction('navigation', { path: location.pathname });
          }
        } else {
          notifyAction('navigation', { path: location.pathname });
        }
      }
    } else {
      prevStepIndexRef.current = currentStepIndex;
    }
  }, [isActive, currentStep, currentStepIndex, location.pathname, notifyAction, skipNextNavCheck, setSkipNextNavCheck]);

  // Calculate tooltip position
  const getTooltipStyle = useCallback(() => {
    const padding = 16;

    if (!currentStep || currentStep.placement === 'center' || !targetRect) {
      // Center the tooltip
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const tooltipWidth = 320;
    const tooltipHeight = 200; // Approximate
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    const isMobile = viewport.width <= 480;

    let style = {
      position: 'fixed',
    };

    // On mobile, for 'left' placement, position above the target to avoid covering it
    if (isMobile && currentStep.placement === 'left') {
      // Position above the target element
      style.bottom = viewport.height - targetRect.top + padding;
      style.left = padding;
      style.right = padding;
      style.transform = 'none';
      return style;
    }

    switch (currentStep.placement) {
      case 'top':
        style.bottom = viewport.height - targetRect.top + padding;
        style.left = targetRect.left + targetRect.width / 2;
        style.transform = 'translateX(-50%)';
        break;
      case 'bottom':
        style.top = targetRect.bottom + padding;
        style.left = targetRect.left + targetRect.width / 2;
        style.transform = 'translateX(-50%)';
        break;
      case 'left':
        // Calculate the desired right edge position
        const desiredRight = viewport.width - targetRect.left + padding;
        // Check if tooltip would go off left edge of screen
        const leftEdge = viewport.width - desiredRight - tooltipWidth;
        if (leftEdge < padding) {
          // Not enough room on left, position above instead
          style.bottom = viewport.height - targetRect.top + padding;
          style.left = targetRect.left + targetRect.width / 2;
          style.transform = 'translateX(-50%)';
        } else {
          style.top = targetRect.top + targetRect.height / 2;
          style.right = desiredRight;
          style.transform = 'translateY(-50%)';
        }
        break;
      case 'right':
        style.top = targetRect.top + targetRect.height / 2;
        style.left = targetRect.right + padding;
        style.transform = 'translateY(-50%)';
        break;
      case 'bottom-left':
        style.top = targetRect.bottom + padding;
        style.right = viewport.width - targetRect.right;
        break;
      case 'bottom-right':
        style.top = targetRect.bottom + padding;
        style.left = targetRect.left;
        break;
      case 'top-left':
        style.bottom = viewport.height - targetRect.top + padding;
        style.right = viewport.width - targetRect.right;
        break;
      case 'top-right':
        style.bottom = viewport.height - targetRect.top + padding;
        style.left = targetRect.left;
        break;
      default:
        style.top = '50%';
        style.left = '50%';
        style.transform = 'translate(-50%, -50%)';
    }

    // Ensure tooltip stays within viewport
    if (style.left && typeof style.left === 'number') {
      style.left = Math.max(padding, Math.min(style.left, viewport.width - tooltipWidth - padding));
    }
    if (style.top && typeof style.top === 'number') {
      style.top = Math.max(padding, Math.min(style.top, viewport.height - tooltipHeight - padding));
    }
    // Also check right positioning doesn't push tooltip off left edge
    if (style.right && typeof style.right === 'number') {
      const calculatedLeft = viewport.width - style.right - tooltipWidth;
      if (calculatedLeft < padding) {
        // Switch to left-based positioning
        delete style.right;
        style.left = padding;
      }
    }

    return style;
  }, [currentStep, targetRect]);

  // Handle skip button
  const handleSkip = async () => {
    endTutorial(false);
    await showAlert('You can restart the tutorial anytime from Settings.', {
      title: 'Tutorial Skipped',
    });
  };

  // Handle next button
  const handleNext = () => {
    if (currentStep?.isFinal) {
      endTutorial(true);
    } else {
      nextStep();
    }
  };

  if (!isActive || !currentStep) {
    return null;
  }

  const tooltipStyle = getTooltipStyle();

  const progress = ((currentStepIndex + 1) / totalSteps) * 100;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStep.isFinal || currentStepIndex === totalSteps - 1;
  // Steps with waitFor require user action - hide Next button
  const requiresAction = !!currentStep.waitFor;
  // Steps that allow clicking through the spotlight
  const allowTargetClick = currentStep.allowTargetClick;
  // Steps where tooltip should be hidden (user interacting with modal)
  const shouldHideTooltip = currentStep.hideTooltip;

  // For hideTooltip steps, show a compact floating pill instead
  if (shouldHideTooltip) {
    return (
      <div className="tutorial-floating-pill">
        <span className="tutorial-floating-pill-text">{currentStep.content}</span>
        <button
          className="tutorial-floating-pill-skip"
          onClick={handleSkip}
          aria-label="Skip tutorial"
        >
          Skip ✕
        </button>
      </div>
    );
  }

  // Only hide backdrop when spotlight is actually visible
  // This prevents flash during transitions between steps with targets
  const spotlightIsVisible = isReady && targetRect;

  return (
    <div className="tutorial-overlay" role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
      {/* Persistent backdrop - only goes transparent when spotlight SVG is actually rendered */}
      <div className={`tutorial-backdrop-persistent ${allowTargetClick ? 'allow-click' : ''} ${spotlightIsVisible ? 'has-spotlight' : ''}`} />

      {/* Spotlight and tooltip only render when ready */}
      {isReady && (
        <>
          {/* Spotlight effect for target element */}
          {targetRect && (
            <TutorialSpotlight rect={targetRect} allowClick={allowTargetClick} />
          )}

          {/* Tooltip */}
          <div
            key={currentStep.id}
            ref={tooltipRef}
            className={`tutorial-tooltip ${currentStep.showPremiumComparison ? 'premium-comparison' : ''}`}
            style={tooltipStyle}
          >
        {/* Progress indicator */}
        <div className="tutorial-progress">
          <div className="tutorial-progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Step counter */}
        <div className="tutorial-step-counter">
          Step {currentStepIndex + 1} of {totalSteps}
        </div>

        {/* Content */}
        <h2 id="tutorial-title" className="tutorial-title">{currentStep.title}</h2>
        <p className="tutorial-content">{currentStep.content}</p>

        {/* Premium comparison table */}
        {currentStep.showPremiumComparison && (
          <div className="tutorial-premium-table">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Premium</th>
                </tr>
              </thead>
              <tbody>
                {PREMIUM_COMPARISON.map((row, index) => (
                  <tr key={index}>
                    <td>{row.feature}</td>
                    <td>
                      {typeof row.free === 'boolean' ? (
                        row.free ? (
                          <CheckIcon />
                        ) : (
                          <XIcon />
                        )
                      ) : (
                        row.free
                      )}
                    </td>
                    <td>
                      {typeof row.premium === 'boolean' ? (
                        row.premium ? (
                          <CheckIcon />
                        ) : (
                          <XIcon />
                        )
                      ) : (
                        row.premium
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {currentStep.showAndMore && (
              <div className="tutorial-premium-footer">
                <div className="tutorial-premium-and-more">...and more!</div>
                <div className="tutorial-premium-upgrade">Upgrade anytime in Profile → Settings</div>
              </div>
            )}
          </div>
        )}

        {/* Action hint for steps that require user action */}
        {requiresAction && currentStep.target && (
          <div className="tutorial-action-hint">
            <PointerIcon />
            <span>Tap the highlighted area to continue</span>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="tutorial-buttons">
          {/* Hide skip button on final step */}
          {!isLastStep && (
            <button
              className="tutorial-btn tutorial-btn-skip"
              onClick={handleSkip}
            >
              Skip Tour
            </button>
          )}

          <div className="tutorial-btn-group">
            {!isFirstStep && !requiresAction && !currentStep.disableBack && (
              <button
                className="tutorial-btn tutorial-btn-back"
                onClick={prevStep}
              >
                Back
              </button>
            )}

            {!requiresAction && (
              <button
                className="tutorial-btn tutorial-btn-next"
                onClick={handleNext}
              >
                {isLastStep ? 'Done' : 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

// Pointer icon for action hints
const PointerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M22 14a8 8 0 0 1-8 8" />
    <path d="M18 11v-1a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
    <path d="M14 10V9a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v1" />
    <path d="M10 9.5V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v10" />
    <path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);

// Icon components
const CheckIcon = () => (
  <svg className="tutorial-icon check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg className="tutorial-icon x" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default Tutorial;
